import { appAxiosInstance } from '@/config/axios';
import { getStructuredUserFormsResponse } from '@/models/forms';
import {
	getStructuredCreateDraftFormResponse,
	getStructuredCreateDuplicateFormResponse,
	getStructuredCustomField,
	getStructuredFormEditDetailsResponse,
	getStructuredFormFilledByUserResponse,
	getStructuredGetFormFillingDetailsResponse,
	getStructuredGetFormUsersResponse,
	getStructuredInputField,
} from '@/models/forms/forms';

import { ICompositeAIQuery } from '@/features/ai-assistant/types/ai-types';

import { withFallbackQuickReplies } from '@/shared/constants/quick-replies';

import { PRE_DEFINED_COLUMN_AI_SHORTLISTING_TYPE } from '@/utils/constants';
import { transformKeysSnakeToCamelCase } from '@/utils/transformation';

import {
	ICreateManualSubmissionPayload,
	ISCreateManualSubmissionResponse,
} from '../submissions/submissions.types';
import {
	IAddFormFieldPayload,
	IAddFormFieldResponse,
	IAddFormStagePayload,
	IAddUserToFormPayload,
	IBulkActionPayload,
	IBulkActionResponse,
	ICheckFormEditAccessPayload,
	ICreateDraftFormResponse,
	ICreateDuplicateFormPayload,
	IDeleteFormPayload,
	IDeleteFormFieldPayload,
	IDeleteFormFieldResponse,
	IDeleteFormStagePayload,
	IDeleteUserFromFormPayload,
	IFetchFieldProtectionDetailsPayload,
	IFetchFormCountsPayload,
	IFetchSubmissionCreatorPayload,
	IForm,
	IFormEditDetailsResponse,
	IFormFilledByUserPayload,
	IFormFilledByUserResponse,
	IFormUpdateStatusPayload,
	IGenerateImagePresignedUrlPayload,
	IGenerateImagePresignedUrlResponse,
	IGenerateVideoPresignedUrlPayload,
	IGenerateVideoPresignedUrlResponse,
	IGetColumnInfoFormPayload,
	IGetColumnInfoFormResponse,
	IGetFormAnalyticsPayload,
	IGetFormAnalyticsResponse,
	IGetFormDetailsPayload,
	IGetFormDetailsResponse,
	IGetFormEditDetailsPayload,
	IGetFormFillingDetailsPayload,
	IGetFormFillingDetailsResponse,
	IGetFormNotificationSettingsPayload,
	IGetFormNotificationSettingsResponse,
	IGetFormUsersPayload,
	IGetFormUsersResponse,
	IGetUserFormsPayload,
	ILabelWithColor,
	IQuickReply,
	IRenameFormStagePayload,
	IReorderFormStagePayload,
	ISCheckFormEditAccessResponse,
	ISDeleteSubmissionsPayload,
	ISFetchFieldProtectionDetailsResponse,
	ISFetchFormCountsResponse,
	ISFetchSubmissionCreatorResponse,
	ISForm,
	ISFormCustomField,
	ISFormInputField,
	ISGenerateImagePresignedUrlResponse,
	ISGenerateVideoPresignedUrlResponse,
	ISGetColumnInfoFormResponse,
	ISLabelWithColor,
	ISUpdateFieldProtectionResponse,
	ISUploadCompanyLogoResponse,
	ISUploadTempImageResponse,
	IUpdateDefaultArchiveChatStatusPayload,
	IUpdateDefaultSendMessagePayload,
	IUpdateFieldProtectionPayload,
	IUpdateFormColumnOrderPayload,
	IUpdateFormColumnWidthPayload,
	IUpdateFormEditDetailsPayload,
	IUpdateFormLabelsPayload,
	IUpdateFormNotifsPayload,
	IUpdateNotificationSettingsPayload,
	IUpdatePublicPageVisibilityPayload,
	IUpdateQuickRepliesPayload,
	IUploadTempImageResponse,
	IUserFormsResponse,
	IsUpdatePublicPageVisibilityResponse,
	TFormFastMode,
} from './forms.types';

export async function getUserForms(payload: IGetUserFormsPayload) {
	const res = await appAxiosInstance.post('/form/fetch-for-user', payload);

	const data = res.data as IUserFormsResponse;

	return getStructuredUserFormsResponse(data, payload.page_details);
}

export async function getFormDetails(payload: IGetFormDetailsPayload) {
	const res = await appAxiosInstance.post('/form/fetch-details', payload);

	const data = res.data as IGetFormDetailsResponse;

	return getStructuredForm(data);
}

export async function getColumnInfoForm(payload: IGetColumnInfoFormPayload) {
	const res = await appAxiosInstance.post('/form/fetch-column-info', payload);

	const data = res.data as IGetColumnInfoFormResponse;

	return getStructuredColumnInfoForm(data);
}

export async function updateFormDefaultArchiveChatStatus(
	payload: IUpdateDefaultArchiveChatStatusPayload
) {
	const res = await appAxiosInstance.post('/form/update-default-archive', payload);

	return res.data as string;
}

export async function updateFormDefaultSendMessage(payload: IUpdateDefaultSendMessagePayload) {
	const res = await appAxiosInstance.post('/form/update-default-send-message', payload);

	return res.data as string;
}

export async function createDraftForm(payload: { onboarding?: boolean }) {
	const res = await appAxiosInstance.post('/form/create-draft-form', payload);

	const data = res.data as ICreateDraftFormResponse;

	return getStructuredCreateDraftFormResponse(data);
}

export async function getFormEditDetails(payload: IGetFormEditDetailsPayload) {
	const res = await appAxiosInstance.post('/form/fetch-for-edit', payload);

	const data = res.data as IFormEditDetailsResponse;

	return getStructuredFormEditDetailsResponse(data);
}

export async function updateFormEditDetails(payload: IUpdateFormEditDetailsPayload) {
	const res = await appAxiosInstance.post('/form/edit', payload);

	const data = res.data as IFormEditDetailsResponse;

	return getStructuredFormEditDetailsResponse(data);
}

export async function updateFormStatus(payload: IFormUpdateStatusPayload) {
	const res = await appAxiosInstance.post('/form/update-status', payload);

	return res.data as string;
}

export async function updatePublicPageVisibility(payload: IUpdatePublicPageVisibilityPayload) {
	const res = await appAxiosInstance.post('/form/update-public-page-visibility', payload);

	return transformKeysSnakeToCamelCase(res.data) as unknown as IsUpdatePublicPageVisibilityResponse;
}

export async function updateQuickReplies(payload: IUpdateQuickRepliesPayload) {
	const res = await appAxiosInstance.post('/form/update-quick-replies', payload);
	return res.data.quick_replies as IQuickReply[];
}
export async function getFormFillingDetails(payload: IGetFormFillingDetailsPayload) {
	const res = await appAxiosInstance.post(`/form/fetch-for-filling`, payload);

	const data = res.data as IGetFormFillingDetailsResponse;

	return getStructuredGetFormFillingDetailsResponse(data);
}

export async function formFilledByUser(payload: IFormFilledByUserPayload) {
	const res = await appAxiosInstance.post(`/form/already-filled-by-user`, payload);

	const data = res.data as IFormFilledByUserResponse;

	return getStructuredFormFilledByUserResponse(data);
}

export async function createDuplicateForm(payload: ICreateDuplicateFormPayload) {
	const res = await appAxiosInstance.post(`/form/duplicate`, payload);

	return getStructuredCreateDuplicateFormResponse(res.data);
}

export async function updateFormNotifs(payload: IUpdateFormNotifsPayload) {
	const res = await appAxiosInstance.post(`/form/update-notifs`, payload);
	return res.data;
}

export async function getFormUsers(payload: IGetFormUsersPayload) {
	const res = await appAxiosInstance.post(`/form/fetch-users`, payload);

	const data = res.data as IGetFormUsersResponse;

	return getStructuredGetFormUsersResponse(data);
}

export async function getFormNotificationSettings(payload: IGetFormNotificationSettingsPayload) {
	const res = await appAxiosInstance.post(`/form/fetch-notif-settings`, payload);

	return res.data as IGetFormNotificationSettingsResponse;
}

export async function updateNotificationSettings(payload: IUpdateNotificationSettingsPayload) {
	const res = await appAxiosInstance.post(`/form/update-notif-settings`, payload);
	return res.data;
}

export async function addUserToForm(payload: IAddUserToFormPayload) {
	const res = await appAxiosInstance.post(`/form/add-user`, payload);

	const data = res.data as IGetFormUsersResponse;

	return getStructuredGetFormUsersResponse(data);
}
export interface IUpdateSubmissionLabelsPayload {
	form_id: string;
	submission_id: string;
	labels: string[];
}
export async function handleUpdateSubmissionLabels(
	payload: IUpdateSubmissionLabelsPayload,
	submissionId: string
) {
	if (!submissionId) return;

	const res = await appAxiosInstance.post('/submission/update-labels', payload);

	return res.data as string;
}

export async function handleUpdateFormLabels(payload: IUpdateFormLabelsPayload) {
	const res = await appAxiosInstance.post('/form/update-labels', payload);
	return res.data as string;
}

export async function deleteUserFromForm(payload: IDeleteUserFromFormPayload) {
	const res = await appAxiosInstance.post('/form/remove-user', payload);

	return res.data as string;
}

export interface IUpdateSubmissionLabelsPayload {
	form_id: string;
	submission_id: string;
	labels: string[];
}

export async function getFormAnalytics(payload: IGetFormAnalyticsPayload) {
	const res = await appAxiosInstance.post(`/form/fetch-analytics`, payload);
	const data = res.data as IGetFormAnalyticsResponse;

	return data;
}

export async function deleteForm(payload: IDeleteFormPayload) {
	const res = await appAxiosInstance.post('/form/delete', payload);

	return res.data as string;
}

export async function addFormStage(payload: IAddFormStagePayload) {
	const res = await appAxiosInstance.post('/form/add-stage', payload);

	return res.data as string;
}

export async function deleteFormStage(payload: IDeleteFormStagePayload) {
	const res = await appAxiosInstance.post('/form/remove-stage', payload);

	return res.data as string;
}

export async function renameFormStage(payload: IRenameFormStagePayload) {
	const res = await appAxiosInstance.post('/form/rename-stage', payload);

	return res.data as string;
}

export async function reorderFormStages(payload: IReorderFormStagePayload) {
	const res = await appAxiosInstance.post('/form/reorder-stages', payload);

	return res.data as string;
}

export const fetchFormCounts = async (
	payload: IFetchFormCountsPayload
): Promise<ISFetchFormCountsResponse> => {
	const res = await appAxiosInstance.post('/form/fetch-counts', payload);

	return transformKeysSnakeToCamelCase(res.data) as unknown as ISFetchFormCountsResponse;
};
const debounceTimeouts: Record<string, NodeJS.Timeout> = {};

export async function updateFormColumnOrder(
	payload: IUpdateFormColumnOrderPayload
): Promise<string> {
	const requestKey = JSON.stringify(payload);

	if (debounceTimeouts[requestKey]) {
		clearTimeout(debounceTimeouts[requestKey]);
	}

	return new Promise<string>((resolve) => {
		debounceTimeouts[requestKey] = setTimeout(async () => {
			try {
				const res = await appAxiosInstance.post('/form/update-column-order', payload);
				resolve(res.data as string);
			} catch (error) {
				resolve('');
			} finally {
				delete debounceTimeouts[requestKey];
			}
		}, 300);
	});
}

const throttleWidthTimestamps: Record<string, number> = {};

export async function updateFormColumnWidth(
	payload: IUpdateFormColumnWidthPayload
): Promise<string> {
	// Use a stable key (e.g., columnId) instead of JSON.stringify(payload)
	const requestKey =
		payload?.custom_fields?.[0]?.custom_field_id ?? payload?.input_fields?.[0]?.field_id ?? '';
	const now = Date.now();

	// If the last update for this column was less than 300ms ago, skip this call
	if (throttleWidthTimestamps[requestKey] && now - throttleWidthTimestamps[requestKey] < 300) {
		return '';
	}

	throttleWidthTimestamps[requestKey] = now;

	try {
		const res = await appAxiosInstance.post('/form/update-column-width', payload);
		return res.data as string;
	} catch (error) {
		console.error('Failed to update column width:', error);
		return '';
	}
}

export async function addFormField(payload: IAddFormFieldPayload): Promise<ISFormInputField> {
	const res = await appAxiosInstance.post('/form/add-field', payload);

	const data = res.data as IAddFormFieldResponse;

	return getStructuredInputField(data.field);
}

export async function deleteFormField(payload: IDeleteFormFieldPayload): Promise<boolean> {
	const res = await appAxiosInstance.post('/form/delete-field', payload);

	const data = res.data as IDeleteFormFieldResponse;

	return data.deleted;
}

export async function bulkAction(payload: IBulkActionPayload): Promise<IBulkActionResponse> {
	const res = await appAxiosInstance.post('/form/bulk-action', payload);
	return res.data;
}

export function getStructuredLabelWithColor(obj: ILabelWithColor): ISLabelWithColor {
	return {
		value: obj.value,
		colour: obj.colour,
	};
}

export function getStructuredForm(obj: IForm): ISForm {
	return {
		identifier: obj.identifier,
		name: obj.name,
		metaImage: obj.meta_image,
		aiNudgeShown: obj.ai_nudge_shown,
		multipleSubmissions: obj.multiple_submissions,
		emailVerificationRequired: obj.email_verification_required,
		getVerifiedApplicantsOnly: obj.email_verification_required,
		showLabelsRow: obj.show_labels_row,
		status: obj.status,
		formCounts: {
			respondedCount: obj.form_counts?.responded_count ?? obj.counts?.responded_count ?? 0,
			totalCount: obj.form_counts?.total_count ?? obj.counts?.total_count ?? 0,
			statusCounts: obj.form_counts?.status_counts ?? obj.counts?.status_counts ?? [],
		},
		formSuccessMessage: obj.form_success_message,
		stages: obj.stages ?? [],
		lastUpdatedTimestamp: obj.last_updated_timestamp ?? '',
		formToken: obj.form_token ?? '',
		pageHandle: obj.page_handle ?? '',
		inputFields: obj.input_fields?.map((inputField) => getStructuredInputField(inputField)) ?? [],
		customFields:
			obj.custom_fields?.map((customField) => getStructuredCustomField(customField)) ?? [],
		labels: obj.labels ?? [],
		labelsWithColor: obj.labels_wc?.map((label) => getStructuredLabelWithColor(label)) ?? [],
		quickReplies: withFallbackQuickReplies(obj.quick_replies),
		defaultArchiveChatStatus: obj.default_archive,
		closingDate: obj.closing_date,
		includedWidgets: obj.included_widgets ?? [],
		pageId: obj.page_id,
		pageName: obj.page_name,
		unarchivedReplyNotif: obj.unarchived_reply_notif,
		showOnPublicPage: obj.public_page_visibility,
		linkedinEnabled: obj.linkedin_enabled,
		linkedinExcludedReason: obj.linkedin_excluded_reason,
		aiEnabled: obj.ai_enabled,
		aiReviewDefault: obj.ai_review_default,
		bulkActionEnabled: obj.bulk_action_enabled,
		fastMode: obj.fast_mode ?? 'DEFAULT',
		savedFilter: obj.saved_filter ?? null,
	};
}

export function getCustomFieldFromAiQuery(obj: ICompositeAIQuery): ISFormCustomField[] {
	return (
		obj.breakdownQueries?.map((query) => ({
			id: query.id,
			type: PRE_DEFINED_COLUMN_AI_SHORTLISTING_TYPE,
			question: query.name || query.query,
			required: true,
			valid: true,
		})) || []
	);
}

export function getStructuredColumnInfoForm(
	obj: IGetColumnInfoFormResponse
): ISGetColumnInfoFormResponse {
	return {
		deletedFields: obj.deleted_fields,
		labelsPresent: obj.labels_present,
		commentsPresent: obj.comments_present,
		messagesPresent: obj.messages_present,
	};
}

export interface IUpdateFormFastModePayload {
	form_id: string;
	fast_mode: TFormFastMode;
}

export async function updateFormFastMode(payload: IUpdateFormFastModePayload): Promise<string> {
	const res = await appAxiosInstance.post('/form/edit-fast-mode', payload);
	return res.data as string;
}

export async function uploadCompanyLogo(payload: FormData): Promise<ISUploadCompanyLogoResponse> {
	const res = await appAxiosInstance.post('/form/update-logo', payload);
	return transformKeysSnakeToCamelCase(res.data) as unknown as ISUploadCompanyLogoResponse;
}

export async function uploadTempImage(payload: FormData): Promise<ISUploadTempImageResponse> {
	const res = await appAxiosInstance.post('/form/upload-temp-image', payload);
	const data = res.data as IUploadTempImageResponse;
	return {
		imageUrl: data.image_url,
	};
}

export async function generateVideoPresignedUrl(payload: IGenerateVideoPresignedUrlPayload) {
	const res = await appAxiosInstance.post('/form/generate-video-presigned-url', payload);

	const data = res.data as IGenerateVideoPresignedUrlResponse;

	return {
		presignedUrl: data.presigned_url,
	} as ISGenerateVideoPresignedUrlResponse;
}

export async function generateImagePresignedUrl(payload: IGenerateImagePresignedUrlPayload) {
	const res = await appAxiosInstance.post('/form/generate-image-presigned-url', payload);

	const data = res.data as IGenerateImagePresignedUrlResponse;

	return {
		presignedUrl: data.presigned_url,
		fileUrl: data.file_url,
	} as ISGenerateImagePresignedUrlResponse;
}

export async function createManualSubmission(payload: ICreateManualSubmissionPayload) {
	const res = await appAxiosInstance.post('/submission/create-submission-for-admin', payload);
	return transformKeysSnakeToCamelCase(res.data) as unknown as ISCreateManualSubmissionResponse;
}

export async function deleteSubmissions(payload: ISDeleteSubmissionsPayload) {
	const res = await appAxiosInstance.post('/submission/delete-submissions', payload);
	return transformKeysSnakeToCamelCase(res.data) as unknown as boolean;
}

export async function fetchSubmissionCreator(
	payload: IFetchSubmissionCreatorPayload
): Promise<ISFetchSubmissionCreatorResponse> {
	const res = await appAxiosInstance.post('/submission/fetch-creator', payload);

	return transformKeysSnakeToCamelCase(res.data) as unknown as ISFetchSubmissionCreatorResponse;
}

export async function checkFormEditAccess(
	payload: ICheckFormEditAccessPayload
): Promise<ISCheckFormEditAccessResponse> {
	const res = await appAxiosInstance.post('/form/check-edit-access', payload);

	return transformKeysSnakeToCamelCase(res.data) as unknown as ISCheckFormEditAccessResponse;
}

// Column Protection APIs
export async function fetchFieldProtectionDetails(
	payload: IFetchFieldProtectionDetailsPayload
): Promise<ISFetchFieldProtectionDetailsResponse> {
	const res = await appAxiosInstance.post('/form/fetch-field-protection-details', payload);
	return transformKeysSnakeToCamelCase(
		res.data
	) as unknown as ISFetchFieldProtectionDetailsResponse;
}

export async function updateFieldProtection(
	payload: IUpdateFieldProtectionPayload
): Promise<ISUpdateFieldProtectionResponse> {
	const res = await appAxiosInstance.post('/form/update-field-protection', payload);
	return transformKeysSnakeToCamelCase(res.data) as unknown as ISUpdateFieldProtectionResponse;
}
